import { Injectable } from "@angular/core";
import { Self } from "app/shared/model/link.model";
import { Utils } from "ng/src/app/core/utils";
import { DestroyableComponent } from "ng/src/app/shared/destroyable.component";
import { Nullish, PartialExcept } from "ng/src/app/shared/model/app.model";
import { SegmentEditorForm } from "ng/src/app/templates/segment-editor/segment-editor-form";
import { Template, TemplateSegment, TemplateSegmentTypeId } from "ng/src/app/templates/shared/template.model";
import { TemplateService } from "ng/src/app/templates/shared/template.service";
import { map, switchMap } from "rxjs/operators";

@Injectable()
export class SegmentEditorService extends DestroyableComponent {
	
	constructor(
		private templateService: TemplateService
	) {
		super();
	}
	
	save(segment: TemplateSegmentEditorType, form: SegmentEditorForm) {
		const values: Partial<TemplateSegmentEditorType> = Utils.arrayToMap(Object.entries(form.controls), entry => {
			const key = entry[0] as keyof typeof form.controls;
			const value = entry[1] as typeof form.controls[typeof key];
			return [key, value?.value ?? null];
		});
		
		switch (segment.type.id) {
			case TemplateSegmentTypeId.SIGNOFF: {
				if (values.allowAnyUser) {
					values.users = [];
					values.userGroups = [];
					values.requireAllSignoffs = false;
				}
			}
		}
		
		// New Segment setup
		if (!Utils.isValidNumber(segment.id)) {
			values.type = segment.type;
			values.template = segment.template;
			if (Utils.isValidNumber(segment.prevItemId)) {
				values.prevItemId = segment.prevItemId;
			}
		}
		
		const mergedValues = this.mergeSelf(values);
		if (segment.id) {
			return this.templateService.updateTemplateSegment(segment as TemplateSegment, mergedValues);
		}
		
		return this.templateService.postTemplateSegment(mergedValues).pipe(
			switchMap(id => {
				return this.templateService.queryTemplateSegments({ id })
					.pipe(map(res => res[0]))
			})
		);
	}
	
	private mergeSelf<T = any>(input: T): SelfMerged<T> {
		return Utils.arrayToMap(Object.entries(input), (entry) => {
			const key = entry[0] as keyof T;
			let value: Nullish<string | string[] | T[keyof T]> = entry[1] as T[typeof key];
			
			if (value && typeof value === 'object' && '_links' in value) {
				value = mapToSelf(value as unknown as HasSelf);
			}
			
			if (value != null) {
				if (Array.isArray(value)) {
					value = value.map(v => mapIfNeeded(v))
				}
				else {
					value = mapIfNeeded(value);
				}
			}
			
			return [key as string, value];
			
			function mapIfNeeded<T = any>(value: T): T | string {
				if (value != null && typeof value === 'object' && '_links' in value) {
					return (value as unknown as HasSelf)._links.self.href;
				}
				return value;
			}
			
			function mapToSelf(value: HasSelf) {
				return value._links.self.href;
			}
		}) as SelfMerged<T>;
	}
}

export type TemplateSegmentEditorType<Type extends TemplateSegmentTypeId = TemplateSegmentTypeId> = PartialExcept<TemplateSegment<Type>, 'type'> &
	Partial<{ prevItemId: number }> & { template: Template }

type HasSelf = { _links: Self };
type SelfMerged<T> = {
	[U in keyof T]: T[U] extends HasSelf ? string :
					T[U] extends Nullish<HasSelf> ? Nullish<string> :
					T[U] extends HasSelf[] ? string[] :
					T[U] extends Nullish<HasSelf> ? Nullish<string[]> :
					T[U]
}